import ApiService from "@/api";
import UserShortItem from "@/api/SchedulerAdmin/dataSource/api/scheduler/manager/serverless/i-services/models/users/UserShortItem";
import {
  TirPmNotificationModel,
  TirPmStatusBgEnum,
  TirPmStatusTextEnum,
} from "tir-components";
import { ref } from "vue";
import UserDateModel from "../job/models/UserDate";
import UserModel from "@/models/UserModel";
import DropdownItemModel from "../_uc/dropdown-multiselect/dropdownModel";
import EnumJobStatus from "@/api/SchedulerAdmin/dataSource/api/scheduler/manager/serverless/database/enums/EnumJobStatus";
import EnumJobContainerStatus from "@/api/SchedulerAdmin/dataSource/api/scheduler/manager/serverless/database/enums/EnumJobContainerStatus";

export default class BaseAdapterService<T> {
  protected static api: ApiService;
  isLoading = ref(false);
  hasMore = ref(true);

  constructor() {
    if (!BaseAdapterService.api) {
      BaseAdapterService.api = new ApiService();
    }
  }

  showError(message: string) {
    console.error(message);
    this.sendNotification(message, "error");
  }

  showMessage(message: string) {
    this.sendNotification(message, "success");
  }
  /**
   * Проверяет дату на валидность (предполагается UTC) и возвращает её в локальном часовом поясе.
   * @param date - строка в формате UTC (например, "2025-03-10T12:00:00Z") или другой тип
   * @returns Date | undefined - объект Date в локальном часовом поясе или undefined, если дата невалидна
   */
  parseValidDate(date: any): Date | undefined {
    if (!date) return undefined;

    let parsed: Date;

    // Если строка, интерпретируем как UTC
    if (typeof date === "string") {
      // Если нет явного указания UTC, добавляем "Z"
      if (!date.endsWith("Z") && !date.includes("+")) {
        date += "Z";
      }
      parsed = new Date(date);
    } else {
      // Для чисел (timestamp) или других типов
      parsed = new Date(date);
    }

    // Проверяем валидность
    if (isNaN(parsed.getTime())) {
      return undefined;
    }

    // Возвращаем объект Date, который будет отображаться в локальном часовом поясе
    return parsed;
  }

  /**
   *
   * @param target вернуть разницу между текущей датой и target
   * @returns
   */
  timeAgo = (target: Date | string): string => {
    if (!target) return "--";
    const targetDate = typeof target === "string" ? new Date(target) : target;
    if (isNaN(targetDate.getTime())) return "Некорректная дата"; // Проверка на валидность даты

    const now = Date.now();
    const diffMs = targetDate.getTime() - now;
    const diffSec = Math.abs(Math.floor(diffMs / 1000));
    const diffMin = Math.abs(Math.floor(diffSec / 60));
    const diffHour = Math.abs(Math.floor(diffMin / 60));

    return diffMs < 0
      ? diffHour > 0
        ? `${diffHour} ч назад`
        : diffMin > 0
        ? `${diffMin} мин назад`
        : `${diffSec} сек назад`
      : diffHour > 0
      ? `через ${diffHour} ч`
      : diffMin > 0
      ? `через ${diffMin} мин`
      : `через ${diffSec} сек`;
  };
  getUserDate = (user: UserShortItem): UserDateModel => {
    if (!user) {
      return null;
    }
    return new UserDateModel({
      User: new UserModel({
        Id: user.Id,
        FirstName: user.FirstName,
        LastName: user.LastName,
        MiddleName: "",
        ImageUrl: user.ProtoUrl,
        AvatarColor: user.AvatarColor,
      }),
      Date: new Date(),
    });
  };
  private sendNotification(
    msg: string,
    typeMsg: "success" | "error" | "info" | "warn" = "success"
  ) {
    const event = new CustomEvent("test-manager-notification", {
      detail: new TirPmNotificationModel({
        title: msg,
        type: typeMsg,
        group: "general",
        life: 2000,
        closable: true,
      }),
    });
    document.dispatchEvent(event);
  }

  handleScroll(value, loadMore) {
    if (!value) return;
    const { scrollTop, scrollHeight, clientHeight } = value.target;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      loadMore();
    }
  }
  getJobStatuses = ref([
    new DropdownItemModel({
      Id: "0", //Disabled
      Name: "Отключено",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgNeutral,
      Color: TirPmStatusTextEnum.Neutral,
    }),
    new DropdownItemModel({
      Id: "1", //Wait
      Name: "Ожидание",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgAttention,
      Color: TirPmStatusTextEnum.Attention,
    }),
    new DropdownItemModel({
      Id: "2", //Queued
      Name: "В очереди",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgPositive,
      Color: TirPmStatusTextEnum.Positive,
    }),
    new DropdownItemModel({
      Id: "3", //Preparing
      Name: "Подготовка",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgPositive,
      Color: TirPmStatusTextEnum.Positive,
    }),
    new DropdownItemModel({
      Id: "4", //RunWaiting
      Name: "Ожидание запуска",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgAttention,
      Color: TirPmStatusTextEnum.Attention,
    }),
    new DropdownItemModel({
      Id: "5", //Running
      Name: "Выполняется",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgInfo,
      Color: TirPmStatusTextEnum.Info,
    }),
    new DropdownItemModel({
      Id: "6", //Success
      Name: "Успешно",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgPositive,
      Color: TirPmStatusTextEnum.Positive,
    }),
    new DropdownItemModel({
      Id: "7", //Failed
      Name: "Ошибка",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgNegative,
      Color: TirPmStatusTextEnum.Negative,
    }),
    new DropdownItemModel({
      Id: "8", //Removed
      Name: "Удалено",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgNeutral,
      Color: TirPmStatusTextEnum.Neutral,
    }),
  ]);
  getContainerStatuses = ref([
    new DropdownItemModel({
      Id: "0",
      Name: "Контейнер удален",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgNeutral,
      Color: TirPmStatusTextEnum.Neutral,
    }),
    new DropdownItemModel({
      Id: "1",
      Name: "Создание контейнера",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgAttention,
      Color: TirPmStatusTextEnum.Attention,
    }),
    new DropdownItemModel({
      Id: "2",
      Name: "Ошибка создания контейнера",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgNegative,
      Color: TirPmStatusTextEnum.Negative,
    }),
    new DropdownItemModel({
      Id: "3",
      Name: "Готов",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgPositive,
      Color: TirPmStatusTextEnum.Positive,
    }),
    new DropdownItemModel({
      Id: "4",
      Name: "Выполняется",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgInfo,
      Color: TirPmStatusTextEnum.Info,
    }),
    new DropdownItemModel({
      Id: "5",
      Name: "Завершение контейнера",
      IsDoted: true,
      Bg: TirPmStatusBgEnum.BgAttention,
      Color: TirPmStatusTextEnum.Attention,
    }),
  ]);
  /**
   * получить task статус по id
   * @param id
   * @returns
   */
  getJobStatus(id: EnumJobStatus): DropdownItemModel {
    return this.getJobStatuses.value.find((el) => Number(el.Id) == id);
  }
  /**
   *  получить container статус по id
   * @param id
   * @returns
   */
  getContainerStatus(id: EnumJobContainerStatus): DropdownItemModel {
    return this.getContainerStatuses.value.find((el) => Number(el.Id) == id);
  }
}
